import {
    Credentials,
    LoginResponse,
    PlatformRedirectResponse,
    SignupPayload,
    SignupResponse,
} from '@quadminds/client-apis/auth';
import { FLASH, SAAS } from '@quadminds/shared/commons';
import { isValidUrl } from '@quadminds/shared/utils';
import axios, { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import { EmailAuthProvider, UserCredential } from 'firebase/auth';
import { AuthApiError } from './auth-api-error';
import { IAuthService } from './interface';
import { AuthPromise, HandleRedirectPayload } from './types';
import { PLATFORM_URL } from '../../config';
import {
    acceptsSaasCode,
    AUTH_API_ERRORS,
    FetchErrorsBigType,
    FIREBASE_AUTH_ERRORS,
    handleLegacyPlatformRedirect,
    IsAxiosForbidden,
    IsAxiosInternalServerError,
    IsAxiosNotFound,
    IsAxiosServiceUnavailable,
    IsAxiosUnAuthorized,
    IsAxiosUnprocessableEntity,
    isBrowserEnvironment,
    isMindy,
    IsMissingInAuthSystem,
    isOms,
    isSaas,
    isStorkUser,
    LegacyLoginError,
    typeOfError,
} from '../../utils';
import { authApi, storkAuthApi } from '../api';
import { FirebaseService } from '../firebase';

// Normalizes third party provider errors into AuthApiError / FirebaseError and rethrows them
function throwNormalizedProviderError(error: unknown): never {
    if (axios.isAxiosError(error)) {
        const data = (error as AxiosError).response?.data as Partial<AuthApiError> | undefined;
        if (data?.code) {
            throw new AuthApiError(data.code, data.message ?? '');
        }
        throw error;
    }
    if (error instanceof Error) {
        // Firebase errors carry a `code` prop
        const err = error as Error & { code: string };
        if (err.code) {
            throw new FirebaseError(err.code, err.message);
        }
    }
    throw error;
}

// Auto-login after signup: retries with delay because the backend may need time to process the new user
async function retryAutoLogin(idToken: string, providerLabel: string, attempts = 2): Promise<void> {
    for (let attempt = 0; attempt < attempts; attempt++) {
        try {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const loginResponse = await authApi.loginThroughThirdParty(idToken);
            localStorage.setItem('token', loginResponse.accessToken);
            localStorage.setItem('refreshToken', loginResponse.refreshToken);
            break;
        } catch {
            console.log(`[SignUp ${providerLabel}] Auto-login attempt ${attempt + 1} failed, retrying...`);
        }
    }
}

async function loginThroughProvider(signIn: () => Promise<UserCredential>): AuthPromise<LoginResponse> {
    try {
        const firebaseResult = (await signIn()) as UserCredential;
        const idToken = await firebaseResult.user.getIdToken();
        const loginResponse: LoginResponse = await authApi.loginThroughThirdParty(idToken);
        localStorage.setItem('token', loginResponse.accessToken);
        localStorage.setItem('refreshToken', loginResponse.refreshToken);
        return loginResponse;
    } catch (error) {
        throwNormalizedProviderError(error);
    }
}

async function signUpWithProvider(
    signIn: () => Promise<UserCredential>,
    inviteUuid: string,
    userExternalId: string,
    providerLabel: string,
): AuthPromise<SignupResponse> {
    try {
        const firebaseResult = (await signIn()) as UserCredential;
        if (!firebaseResult.user.email) {
            throw new Error('Identity provider auth succeeded, but user has a null email value');
        }

        const [firstName, lastName] = firebaseResult.user?.displayName?.split(' ') ?? [];
        const userData: SignupPayload = {
            firstName,
            lastName,
            phone: firebaseResult.user.phoneNumber ?? undefined,
            avatar: firebaseResult.user.photoURL,
            email: firebaseResult.user.email,
            externalId: userExternalId,
        };
        const signupResponse: SignupResponse = await authApi.signup(userData, inviteUuid);

        const idToken = await firebaseResult.user.getIdToken();
        if (isMindy || isOms) {
            await retryAutoLogin(idToken, providerLabel);
        }

        return signupResponse;
    } catch (error: unknown) {
        throwNormalizedProviderError(error);
    }
}

export class AuthService implements IAuthService {
    public async registerWithEmailAndPassword(
        userData: SignupPayload & Credentials,
        inviteUuid: string,
    ): AuthPromise<SignupResponse> {
        const firebaseCredentials: Credentials = { email: userData.email, password: userData.password };
        let firebaseResult: UserCredential | undefined;
        let isExistingFirebaseUser = false;
        try {
            firebaseResult = (await FirebaseService.registerWithEmailAndPassword(
                firebaseCredentials,
            )) as UserCredential;
        } catch (error) {
            const errorCode = (error as FirebaseError)?.code;
            if (errorCode === FIREBASE_AUTH_ERRORS.EMAIL_ALREADY_IN_USE) {
                isExistingFirebaseUser = true;
            } else {
                throw error;
            }
        }
        if (!firebaseResult && !isExistingFirebaseUser) throw new Error('Identity provider user creation error');
        const { password: _pwd, ...withoutPassword } = userData;
        const signupPayload: SignupPayload = withoutPassword;
        try {
            const userApiResult = await authApi.signup(signupPayload, inviteUuid);
            return userApiResult;
        } catch (error) {
            if (!isExistingFirebaseUser && firebaseResult) {
                await firebaseResult.user.delete();
            }
            console.error('Error while creating user', error);
            throw new Error('Error while creating user');
        }
    }

    public loginInWithEmailAndPassword = async (credentials: Credentials): AuthPromise<LoginResponse> => {
        let firebaseIdToken: string | undefined;
        try {
            // try firebase login
            const firebaseResult = (await FirebaseService.logInWithEmailAndPassword(credentials)) as UserCredential;
            if (firebaseResult) {
                // once done get firebase user id token
                const idToken = await firebaseResult.user.getIdToken();
                firebaseIdToken = idToken;
                // login with user auth v2
                const authApiResult: LoginResponse = await authApi.loginWithEmailAndPasswordV2(idToken);

                // save token
                localStorage.setItem('token', authApiResult.accessToken);
                localStorage.setItem('refreshToken', authApiResult.refreshToken);
                return authApiResult;
            }
        } catch (firstError: unknown) {
            // In case of Mindy, if we have a firebase id token but got unauthorized, it means the user may not exist in the platform yet, so we should try to login against stork platform before asking for retry at v1
            if ((isMindy || isOms) && firebaseIdToken) {
                await this.loginAgainstStorkPlatform(firebaseIdToken);
                return;
            }

            // ask for user not found according fetch error type
            const askForRetry = {
                [FetchErrorsBigType.AXIOS_ERROR]: (error: unknown): boolean => {
                    const axiosError = error as AxiosError;
                    const errorCode = (
                        axiosError?.response?.data as { code: AUTH_API_ERRORS } & Record<'string', unknown>
                    )?.code;

                    // Don't retry if user or organization is disabled
                    if (
                        errorCode === AUTH_API_ERRORS.USER_DISABLED_EXCEPTION ||
                        errorCode === AUTH_API_ERRORS.ORGANIZATION_DISABLED_EXCEPTION
                    ) {
                        return false;
                    }

                    return (
                        IsAxiosUnAuthorized(axiosError) ||
                        IsAxiosForbidden(axiosError) ||
                        // In saas, we must consider users that are not yet present in auth-system
                        Boolean(isSaas && IsAxiosNotFound(axiosError)) ||
                        // Is saas, we need a fallback for in case of auth-system failure
                        Boolean(
                            isSaas && (IsAxiosServiceUnavailable(axiosError) || IsAxiosInternalServerError(axiosError)),
                        )
                    );
                },
                [FetchErrorsBigType.FIREBASE_ERROR]: async (error: unknown): Promise<boolean> => {
                    const isUserNotFound = (error as FirebaseError).code === FIREBASE_AUTH_ERRORS.USER_NOT_FOUND;
                    const isWrongPass = (error as FirebaseError).code === FIREBASE_AUTH_ERRORS.WRONG_PASS;
                    const isInvalidEmail = (error as FirebaseError).code === FIREBASE_AUTH_ERRORS.INVALID_EMAIL;
                    const isMissingEmail = (error as FirebaseError).code === FIREBASE_AUTH_ERRORS.MISSING_EMAIL;
                    const isServerError = [
                        FIREBASE_AUTH_ERRORS.INTERNAL_ERROR,
                        FIREBASE_AUTH_ERRORS.NETWORK_REQUEST_FAILED,
                    ].includes((error as FirebaseError).code as FIREBASE_AUTH_ERRORS);

                    if (isWrongPass) {
                        try {
                            const providers = await FirebaseService.checkProvider(credentials.email);
                            return !providers.includes(EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD);
                        } catch (error: unknown) {
                            // Provider lookup failed; treat as "can't confirm mismatch" so caller falls back to normal wrong-password flow
                            console.error(error);
                            return false;
                        }
                    } else if (acceptsSaasCode && (isInvalidEmail || isMissingEmail || isServerError)) {
                        // On SaaS backed platforms, users may attempt to log in with a code instead of an email
                        // Also, in case of firebase error, users should login against platform
                        return true;
                    } else {
                        return isUserNotFound;
                    }
                },
                [FetchErrorsBigType.UNKNOWN_ERROR]: (_error: unknown) => false,
            };
            // Ask if has to retry at API v1

            const hasToReTryWithV1 = await askForRetry[typeOfError(firstError)](firstError);
            if (hasToReTryWithV1) {
                try {
                    // Retry login at API v1
                    const legacyResult: LoginResponse = await authApi.loginWithEmailAndPasswordV1(credentials);
                    localStorage.setItem('token', legacyResult.accessToken);
                    localStorage.setItem('refreshToken', legacyResult.refreshToken);
                    return legacyResult;
                } catch (secondError: unknown) {
                    if (axios.isAxiosError(secondError)) {
                        // ask user has wrong password at v1
                        if (secondError.response?.data?.nextPage) {
                            // Follow redirections to proper error pages,
                            // such as "user blocked by password attemps" or "user suspended", etc
                            window.location.href = secondError.response?.data?.nextPage;
                        } else if (IsAxiosUnAuthorized(secondError) || IsAxiosForbidden(secondError)) {
                            throw new FirebaseError(FIREBASE_AUTH_ERRORS.WRONG_PASS, '401');
                        } else if (IsAxiosUnprocessableEntity(secondError) && IsMissingInAuthSystem(secondError)) {
                            // The user credentials are fine, but it does not exists in the auth system
                            try {
                                await this.loginAgainstSaasPlatform(credentials);
                                return;
                            } catch (legacyError) {
                                // If legacy SaaS login fails but we already have a valid token, allow login to succeed
                                const token = localStorage.getItem('token');
                                const refreshToken = localStorage.getItem('refreshToken');
                                if (token && refreshToken && (isMindy || isOms)) {
                                    console.log('Legacy SaaS login failed, but v2 token is valid. Proceeding.');
                                    return { accessToken: token, refreshToken } as LoginResponse;
                                }
                                // If no token, throw the legacy error
                                throw legacyError;
                            }
                        } else {
                            throw new Error(secondError.message);
                        }
                    } else {
                        // it was an unknown error
                        throw new Error((secondError as Error).message);
                    }
                }
            } else {
                // return error according error type
                if (typeOfError(firstError) === FetchErrorsBigType.AXIOS_ERROR) {
                    const authServiceError = (firstError as AxiosError).response?.data as AuthApiError;
                    throw new AuthApiError(authServiceError.code, authServiceError.message);
                } else if (typeOfError(firstError) === FetchErrorsBigType.FIREBASE_ERROR) {
                    throw new FirebaseError((firstError as FirebaseError).code, (firstError as FirebaseError).message);
                } else {
                    throw new Error((firstError as Error).message);
                }
            }
        }
    };

    public loginAgainstSaasPlatform = async (credentials: Credentials): Promise<void> => {
        const defaultErrorMessage = 'Failed attempt to login';
        try {
            if (!isSaas) throw new Error('Unsupported method');

            const params = new URLSearchParams();
            params.append('codigo', credentials.email);
            params.append('pwd', credentials.password);

            const res = await axios.post<PlatformRedirectResponse>(`${PLATFORM_URL}/login`, params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                withCredentials: true,
            });

            if (res) {
                if (res.data.success && res.data.nextPage) {
                    // Redirect now to saas platform, because the user may not exist in the auth-system
                    // and later methods of the flow would fail
                    window.location.href = res.data.nextPage;
                } else if (res.data.error && res.data.nextPage) {
                    console.error(res.data);
                    window.location.href = res.data.nextPage;
                } else if (res.data.errorCode) {
                    throw new LegacyLoginError(res.data.errorCode, res.data.error ?? defaultErrorMessage);
                }
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401 && error.response.data?.nextPage) {
                // Redirect to the next page if 401 and nextPage is present
                window.location.href = error.response.data.nextPage;
            } else if (axios.isAxiosError(error) && error.response?.data?.errorCode) {
                throw new LegacyLoginError(
                    error.response.data.errorCode,
                    error.response.data.error ?? defaultErrorMessage,
                );
            }
            console.log(defaultErrorMessage, error);
        }
    };

    public loginAgainstStorkPlatform = async (idToken: string): Promise<LoginResponse | void> => {
        if (!isMindy && !isOms) throw new Error('Unsupported method');
        const loginResponse = await storkAuthApi.loginWithEmailAndPasswordV2(idToken);
        localStorage.setItem('token', loginResponse.accessToken);
        localStorage.setItem('refreshToken', loginResponse.refreshToken);
        localStorage.setItem('isStorkUser', 'true');
        return loginResponse;
    };

    public async signUpWithGoogle(
        inviteUuid: string,
        userExternalId: string,
        email?: string,
    ): AuthPromise<SignupResponse> {
        try {
            // 1) Signs in with google creates user in firebase db
            const firebaseResult = (await FirebaseService.signInWithGoogle(email)) as UserCredential;
            if (!firebaseResult.user.email) {
                throw new Error('Identity provider auth succeeded, but user has a null email value');
            }

            // 2) Registers user in users API db with provider´s user profile
            const [firstName, lastName] = firebaseResult.user?.displayName?.split(' ') ?? [];
            const userData: SignupPayload = {
                firstName,
                lastName,
                phone: firebaseResult.user.phoneNumber ?? undefined,
                avatar: firebaseResult.user.photoURL,
                email: firebaseResult.user.email,
                externalId: userExternalId,
            };
            const signupResponse: SignupResponse = await authApi.signup(userData, inviteUuid);

            const idToken = await firebaseResult.user.getIdToken();
            if (isMindy || isOms) {
                await retryAutoLogin(idToken, 'Google');
            }

            return signupResponse;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const authServiceError = (error as AxiosError).response?.data as AuthApiError;
                throw new AuthApiError(authServiceError.code, authServiceError.message);
            } else if (error instanceof Error) {
                // Firebase errors has code as prop
                const err = error as Error & { code: string };
                if (err.code) {
                    const errorCode = err.code;
                    const errMessage = err.message;
                    throw new FirebaseError(errorCode, errMessage);
                }
            }
        }
    }

    public signUpWithMicrosoft(inviteUuid: string, userExternalId: string): AuthPromise<SignupResponse> {
        return signUpWithProvider(() => FirebaseService.signInWithMicrosoft(), inviteUuid, userExternalId, 'Microsoft');
    }

    public signUpWithApple(inviteUuid: string, userExternalId: string): AuthPromise<SignupResponse> {
        return signUpWithProvider(() => FirebaseService.signInWithApple(), inviteUuid, userExternalId, 'Apple');
    }

    public async loginThroughGoogle(): AuthPromise<LoginResponse> {
        try {
            const firebaseResult = (await FirebaseService.signInWithGoogle()) as UserCredential;
            const idToken = await firebaseResult.user.getIdToken();
            const loginResponse: LoginResponse = await authApi.loginThroughThirdParty(idToken);
            localStorage.setItem('token', loginResponse.accessToken);
            localStorage.setItem('refreshToken', loginResponse.refreshToken);
            return loginResponse;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const authServiceError = (error as AxiosError).response?.data as AuthApiError;
                throw new AuthApiError(authServiceError.code, authServiceError.message);
            } else if (error instanceof Error) {
                // Firebase errors has code as prop
                const err = error as Error & { code: string };
                if (err.code) {
                    const errorCode = err.code;
                    const errMessage = err.message;
                    throw new FirebaseError(errorCode, errMessage);
                }
            }
        }
    }

    public loginThroughMicrosoft(): AuthPromise<LoginResponse> {
        return loginThroughProvider(() => FirebaseService.signInWithMicrosoft());
    }

    public loginThroughApple(): AuthPromise<LoginResponse> {
        return loginThroughProvider(() => FirebaseService.signInWithApple());
    }

    private buildRedirectUrl(url: string) {
        const isLocalRedirect = url.includes('localhost') || url.includes('local.quadminds.com');
        if (!isLocalRedirect) {
            return url;
        }
        if (url.includes('?')) {
            return `${url}&token=${localStorage.getItem('token')}`;
        } else {
            return `${url}?token=${localStorage.getItem('token')}`;
        }
    }
    // Once authenticated and requested redirect URL, handle next auth step
    public handleRedirect = async ({
        redirectResponse,
        hadPreviousValidToken,
        redirectUrlQueryParam,
    }: HandleRedirectPayload) => {
        // Validate
        if (!isBrowserEnvironment()) throw new Error('Environment is not supported');

        let redirectPage: string | undefined = redirectResponse.url;

        // Handle each app or platform accordingly
        if (
            (redirectResponse.url.includes(SAAS) ||
                redirectResponse.url.includes(FLASH) ||
                // Para evitar que haga match con urls como la de stork-test.quadminds.com
                redirectResponse.url.includes('https://test.quadminds')) &&
            !redirectUrlQueryParam
        ) {
            redirectPage = await handleLegacyPlatformRedirect(redirectResponse.url, hadPreviousValidToken);
            // If legacy platform redirect returns undefined but we have a valid token and isMindy, use original URL
            // This prevents blocking redirect when legacy platform session init fails but v2 auth succeeded
            if (!redirectPage && localStorage.getItem('token') && (isMindy || isOms)) {
                redirectPage = redirectResponse.url;
            }
        }

        // Check if redirect url param supplied and use it
        if (redirectUrlQueryParam && isValidUrl(redirectUrlQueryParam)) {
            redirectPage = this.buildRedirectUrl(redirectUrlQueryParam);
        }

        // When on Mindy and user authenticated via Stork, always land on Mindy app
        if (isMindy && isStorkUser() && !redirectUrlQueryParam) {
            redirectPage = `${PLATFORM_URL}/apps/mindy-app`;
        }

        // OMS: sin redirect param (entraste directo al dominio tms), aterrizá en la app OMS
        // en vez de la plataforma default del usuario (SaaS/Stork). PLATFORM_URL = tms[-test].quadminds.com.
        // Trailing slash obligatorio: /apps/oms/ sirve el index del bucket; /apps/oms (sin slash) cae al landing → 404.
        if (isOms && !redirectUrlQueryParam) {
            redirectPage = `${PLATFORM_URL}/apps/oms/`;
        }

        // Do nothing if no redirect page
        if (!redirectPage) return;
        console.log('Redirecting to:', redirectPage);
        window.location.href = redirectPage;
    };

    public async resetPassword(email: string) {
        if (isSaas) {
            try {
                const response = await authApi.sendRecoveryEmailV1(email);
                if (response.success) {
                    return true;
                }
            } catch (error) {
                // More specific user's feedback is not recomended in this case.
                console.log('Error trying to send email', error);
            }
        }

        return await FirebaseService.sendPasswordReset(email);
    }

    public logout() {
        console.log('Logout Called');
        localStorage.removeItem('token');
        return FirebaseService.logout();
    }
}
